import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Landmark, Upload, Plug, ShieldCheck, Link2, RefreshCw, KeyRound, Lock } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

const money = (v, ccy = "EUR") => (v == null ? "—" : `${Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`);

function ReadOnlyBadge() {
  const { t } = useI18n();
  return (
    <span data-testid="banking-readonly-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
      <Lock className="h-3 w-3" />{t("banking.read_only")}
    </span>
  );
}

function Overview() {
  const { t } = useI18n();
  const { data, isLoading, isError } = useQuery({ queryKey: ["bank-overview"], queryFn: async () => (await api.get("/banking/overview")).data, retry: false });
  if (isLoading) return <Skeleton className="h-40" />;
  if (isError || !data) return <EmptyState icon={Landmark} title={t("banking.no_accounts")} testId="banking-overview-empty" />;
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="banking-overview">
      <Card className="p-4 md:col-span-2">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("banking.balances")}</h3>
        {Object.keys(data.balances_by_currency).length === 0 ? <p className="text-sm text-slate-500">{t("banking.no_accounts")}</p> :
          <div className="grid gap-3 sm:grid-cols-2">
            {Object.entries(data.balances_by_currency).map(([ccy, v]) => (
              <div key={ccy} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`balance-${ccy}`}>
                <div className="text-xs text-slate-400">{ccy} · {v.accounts} {t("banking.accounts")}</div>
                <div className="text-2xl font-bold tabular-nums">{money(v.current, ccy)}</div>
                <div className="text-xs text-slate-500">{t("banking.available")}: {money(v.available, ccy)}</div>
              </div>
            ))}
          </div>}
      </Card>
      <div className="space-y-3">
        <Card className="p-4"><div className="text-xs text-slate-400">{t("banking.unmatched")}</div><div className="text-2xl font-bold tabular-nums" data-testid="kpi-unmatched">{data.unmatched_transactions}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-400">{t("banking.open_invoices")}</div><div className="text-2xl font-bold tabular-nums">{data.open_invoices}</div></Card>
        {data.warnings.length > 0 && <Card className="p-4 border-amber-300 bg-amber-50 dark:bg-amber-950/40"><div className="text-xs font-semibold text-amber-800 dark:text-amber-300">{t("banking.warnings")}: {data.warnings.length}</div></Card>}
      </div>
    </div>
  );
}

function Accounts() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["bank-accounts"], queryFn: async () => (await api.get("/banking/accounts")).data, retry: false });
  if (isLoading) return <Skeleton className="h-40" />;
  if (!data?.length) return <EmptyState icon={Landmark} title={t("banking.no_accounts")} testId="accounts-empty" />;
  return (
    <Table data-testid="accounts-table"><TableHeader><TableRow>
      <TableHead>IBAN</TableHead><TableHead>{t("banking.owner")}</TableHead><TableHead className="text-right">{t("banking.current")}</TableHead>
      <TableHead className="text-right">{t("banking.available")}</TableHead><TableHead>{t("banking.source")}</TableHead></TableRow></TableHeader>
      <TableBody>{data.map((a) => (<TableRow key={a.id} data-testid={`account-${a.id}`}>
        <TableCell className="font-mono">{a.iban_masked}</TableCell><TableCell>{a.owner_name}</TableCell>
        <TableCell className="text-right tabular-nums">{money(a.balance_current, a.currency)}</TableCell>
        <TableCell className="text-right tabular-nums">{money(a.balance_available, a.currency)}</TableCell>
        <TableCell><span className="rounded bg-slate-100 px-1.5 text-xs dark:bg-slate-800">{a.source}</span></TableCell></TableRow>))}</TableBody></Table>
  );
}

function Transactions() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["bank-txns", q], queryFn: async () => (await api.get("/banking/transactions", { params: { q } })).data, retry: false });
  return (
    <div>
      <Input placeholder={t("banking.search_txn")} value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 max-w-md" data-testid="txn-search" />
      {isLoading ? <Skeleton className="h-40" /> : !data?.length ? <EmptyState icon={RefreshCw} title={t("banking.no_txns")} testId="txns-empty" /> :
        <Table data-testid="transactions-table"><TableHeader><TableRow>
          <TableHead>{t("banking.date")}</TableHead><TableHead>{t("banking.counterparty")}</TableHead><TableHead>{t("banking.reference")}</TableHead>
          <TableHead className="text-right">{t("banking.amount")}</TableHead><TableHead>{t("common.status")}</TableHead></TableRow></TableHeader>
          <TableBody>{data.map((tx) => (<TableRow key={tx.id} data-testid={`txn-${tx.id}`}>
            <TableCell className="tabular-nums">{tx.booking_date}</TableCell><TableCell>{tx.counterparty_name || "—"}</TableCell>
            <TableCell className="max-w-[280px] truncate text-slate-500">{tx.remittance || tx.end_to_end_id}</TableCell>
            <TableCell className={`text-right tabular-nums font-medium ${tx.direction === "credit" ? "text-emerald-600" : "text-red-600"}`}>{tx.direction === "credit" ? "+" : "−"}{money(tx.amount, tx.currency)}</TableCell>
            <TableCell><span className={`rounded-full px-2 py-0.5 text-xs ${tx.recon_status === "matched" ? "bg-emerald-100 text-emerald-800" : tx.recon_status === "partial" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"} dark:bg-slate-800`}>{t(`banking.recon.${tx.recon_status}`)}</span></TableCell></TableRow>))}</TableBody></Table>}
    </div>
  );
}

function Reconciliation() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const { data: accounts = [] } = useQuery({ queryKey: ["bank-accounts"], queryFn: async () => (await api.get("/banking/accounts")).data });
  const [accountIds, setAccountIds] = useState([]);
  const { data: txns } = useQuery({ queryKey: ["bank-recon-queue"], queryFn: async () => (await api.get("/banking/transactions", { params: { status: "unmatched" } })).data });
  const { data: sug } = useQuery({ queryKey: ["bank-sug", sel], enabled: !!sel, queryFn: async () => (await api.get(`/banking/reconciliation/suggestions/${sel}`)).data });

  const accept = async (inv) => {
    try {
      await api.post("/banking/reconciliation/accept", { transaction_id: sel, allocations: [{ einvoice_id: inv.einvoice_id, amount: Math.min(Number(inv.open_amount), Number(sug.remaining_amount)).toFixed(2), score: inv.score }] });
      toast.success(t("banking.matched"));
      qc.invalidateQueries({ queryKey: ["bank-recon-queue"] }); qc.invalidateQueries({ queryKey: ["bank-sug", sel] });
      qc.invalidateQueries({ queryKey: ["bank-overview"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const visibleTxns = (txns || []).filter((tx) => accountIds.length === 0 || accountIds.includes(tx.account_id));

  return (
    <div data-testid="reconciliation-workbench">
      <Card className="mb-4 p-4"><h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("banking.choose_accounts")}</h3><p className="mb-3 text-xs text-slate-500">{t("banking.choose_accounts_hint")}</p><div className="flex flex-wrap gap-2">{accounts.map((a) => <label key={a.id} className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${accountIds.includes(a.id) ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40" : ""}`}><Checkbox checked={accountIds.includes(a.id)} onCheckedChange={(on) => setAccountIds((old) => on ? [...old, a.id] : old.filter((id) => id !== a.id))} /><span className="font-mono">{a.iban_masked}</span><span>{a.owner_name}</span></label>)}</div></Card>
      <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("banking.queue")}</h3>
        {!visibleTxns.length ? <EmptyState icon={ShieldCheck} title={t("banking.queue_empty")} testId="recon-empty" /> :
          <div className="space-y-1">{visibleTxns.map((tx) => (
            <button key={tx.id} onClick={() => setSel(tx.id)} data-testid={`recon-txn-${tx.id}`}
              className={`flex w-full items-center justify-between rounded-md border p-2 text-left text-sm ${sel === tx.id ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 dark:border-slate-700"}`}>
              <span className="truncate">{tx.booking_date} · {tx.counterparty_name || tx.remittance}</span>
              <span className="tabular-nums font-medium">{money(tx.amount, tx.currency)}</span>
            </button>))}</div>}
      </Card>
      <Card className="p-4">
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("banking.suggestions")}</h3>
        {!sel ? <p className="text-sm text-slate-500">{t("banking.pick_txn")}</p> : !sug ? <Skeleton className="h-24" /> :
          <div>
            <p className="mb-2 text-xs text-slate-400">{t("banking.remaining")}: <b className="tabular-nums text-slate-700 dark:text-slate-200">{money(sug.remaining_amount)}</b></p>
            {!sug.suggestions.length ? <p className="text-sm text-slate-500">{t("banking.no_suggestions")}</p> :
              <div className="space-y-2">{sug.suggestions.map((s) => (
                <div key={s.einvoice_id} className="rounded-md border border-slate-200 p-2 dark:border-slate-700" data-testid={`suggestion-${s.einvoice_id}`}>
                  <div className="flex items-center justify-between">
                    <div><span className="font-medium">{s.number}</span> <span className="text-xs text-slate-400">{t("banking.open")}: {money(s.open_amount)}</span></div>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-950 dark:text-blue-300">{Math.round(s.score * 100)}%</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">{s.reasons.map((r) => (<span key={r} className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">{t(`banking.reason.${r}`) || r}</span>))}</div>
                  <Button size="sm" className="mt-2" onClick={() => accept(s)} data-testid={`accept-${s.einvoice_id}`}><Link2 className="h-3 w-3" />{t("banking.accept")}</Button>
                </div>))}</div>}
          </div>}
      </Card>
      </div>
    </div>
  );
}

function Imports({ canImport }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const camtRef = useRef();
  const { data } = useQuery({ queryKey: ["bank-imports"], queryFn: async () => (await api.get("/banking/imports")).data });
  const uploadCamt = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data: r } = await api.post("/banking/import/camt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.duplicate) toast.info(t("banking.import_dup")); else toast.success(`${t("banking.imported")}: +${r.added}`);
      qc.invalidateQueries({ queryKey: ["bank-imports"] }); qc.invalidateQueries({ queryKey: ["bank-txns", ""] });
    } catch (err) { toast.error(apiErrorMessage(err, t)); } finally { if (camtRef.current) camtRef.current.value = ""; }
  };
  return (
    <div>
      {canImport && <div className="mb-4 flex gap-2">
        <input ref={camtRef} type="file" accept=".xml" className="hidden" onChange={uploadCamt} data-testid="camt-file-input" />
        <Button onClick={() => camtRef.current?.click()} data-testid="camt-upload-button"><Upload className="h-4 w-4" />{t("banking.import_camt")}</Button>
        <span className="self-center text-xs text-slate-400">{t("banking.csv_hint")}</span>
      </div>}
      {!data?.length ? <EmptyState icon={Upload} title={t("banking.no_imports")} testId="imports-empty" /> :
        <Table data-testid="imports-table"><TableHeader><TableRow>
          <TableHead>{t("banking.file")}</TableHead><TableHead>{t("banking.kind")}</TableHead><TableHead>SHA-256</TableHead>
          <TableHead className="text-right">{t("banking.added")}</TableHead><TableHead className="text-right">{t("banking.duplicates")}</TableHead></TableRow></TableHeader>
          <TableBody>{data.map((i) => (<TableRow key={i.id} data-testid={`import-${i.id}`}>
            <TableCell className="max-w-[220px] truncate">{i.filename}</TableCell><TableCell><span className="rounded bg-slate-100 px-1.5 text-xs uppercase dark:bg-slate-800">{i.kind}</span></TableCell>
            <TableCell className="font-mono text-xs text-slate-400">{i.sha256?.slice(0, 12)}…</TableCell>
            <TableCell className="text-right tabular-nums">{i.stats?.added ?? "—"}</TableCell><TableCell className="text-right tabular-nums">{i.stats?.duplicates ?? "—"}</TableCell></TableRow>))}</TableBody></Table>}
    </div>
  );
}

function Connections({ canConnect }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["bank-connections"], queryFn: async () => (await api.get("/banking/connections")).data });
  const psd2 = async () => {
    try { const { data: r } = await api.post("/banking/connections/psd2/sandbox", { name: "PSD2 Sandbox" });
      await api.post("/banking/connections/psd2/callback", { connection_id: r.connection_id, state: r.state });
      toast.success(t("banking.psd2_connected")); qc.invalidateQueries({ queryKey: ["bank-connections"] }); qc.invalidateQueries({ queryKey: ["bank-accounts"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const ebics = async () => {
    try { const { data: r } = await api.post("/banking/connections/ebics", { name: "EBICS Sandbox", mode: "sandbox", bank_url: "https://mock-bank.local/ebics", host_id: "EBIXHOST", partner_id: "P1", user_id: "U1" });
      for (const step of ["INI", "HIA", "HPB"]) await api.post(`/banking/connections/ebics/${r.connection_id}/init`, { step });
      await api.post(`/banking/connections/ebics/${r.connection_id}/download`, { iban: "DE02120300000000202051" });
      toast.success(t("banking.ebics_onboarded")); qc.invalidateQueries({ queryKey: ["bank-connections"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  return (
    <div>
      {canConnect && <div className="mb-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={psd2} data-testid="connect-psd2"><Plug className="h-4 w-4" />{t("banking.connect_psd2")}</Button>
        <Button variant="outline" onClick={ebics} data-testid="connect-ebics"><KeyRound className="h-4 w-4" />{t("banking.connect_ebics")}</Button>
      </div>}
      <p className="mb-3 text-xs text-slate-400">{t("banking.sandbox_notice")}</p>
      {!data?.length ? <EmptyState icon={Plug} title={t("banking.no_connections")} testId="connections-empty" /> :
        <div className="space-y-2">{data.map((c) => (
          <Card key={c.id} className="flex items-center justify-between p-3" data-testid={`connection-${c.id}`}>
            <div><div className="font-medium">{c.name} <span className="text-xs uppercase text-slate-400">{c.kind}</span></div>
              <div className="text-xs text-slate-500">{c.provider} · {c.mode}</div></div>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${c.status === "active" || c.status === "sandbox" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"}`}>{t(`banking.conn_status.${c.status}`) || c.status}</span>
          </Card>))}</div>}
    </div>
  );
}

export default function Banking() {
  const { t } = useI18n();
  const { can, modules } = useAuth();
  const tabs = useMemo(() => ["overview", "accounts", "transactions", "reconciliation", "connections", "imports"], []);
  if (!can("banking:view") || !modules?.includes("banking")) {
    return (
      <div>
        <PageHeader title={t("banking.title")} subtitle={t("banking.subtitle")} />
        <EmptyState icon={Lock} title={t("banking.not_available")} testId="banking-not-available" />
      </div>
    );
  }
  return (
    <div>
      <PageHeader title={t("banking.title")} subtitle={t("banking.subtitle")} actions={<ReadOnlyBadge />} />
      <Tabs defaultValue="overview">
        <TabsList data-testid="banking-tabs" className="mb-4 flex flex-wrap">
          {tabs.map((k) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{t(`banking.tab_${k}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="accounts"><Accounts /></TabsContent>
        <TabsContent value="transactions"><Transactions /></TabsContent>
        <TabsContent value="reconciliation"><Reconciliation /></TabsContent>
        <TabsContent value="connections"><Connections canConnect={can("banking:connect")} /></TabsContent>
        <TabsContent value="imports"><Imports canImport={can("banking:import")} /></TabsContent>
      </Tabs>
    </div>
  );
}
